import React, { createContext, useContext, useMemo, useState } from "react";

export type SupportedLanguage = "en" | "de";

type TranslationDict = Record<string, string>;

const dictionaries: Record<SupportedLanguage, TranslationDict> = {
  en: {
    "testFlow.title": "Test Flow",
    "testFlow.subtitle":
      "Assign test requirements to editable legs, define the DUT count, and reorder execution flow with drag and drop.",
    "testFlow.addLeg": "Add Leg",
    "testFlow.addBranch": "Add Branch",
    "testFlow.deleteBranch": "Delete Branch",
    "testFlow.branchLabel": "Branch {{branchName}}",
    "testFlow.sampleBreakouts": "Sample Breakouts",
    "testFlow.sampleBreakoutsTitle": "Sample Breakouts",
    "testFlow.sampleBreakoutsSubtitle":
      "Create sample breakouts for this leg. The quantity total must match the number of DUTs.",
    "testFlow.addSampleBreakout": "Add Sample Breakout",
    "testFlow.noSampleBreakouts":
      "No sample breakouts created for this leg yet.",
    "testFlow.sampleBreakoutName": "Name",
    "testFlow.sampleBreakoutNameHelper":
      "Name is required and must be 255 characters or fewer.",
    "testFlow.sampleBreakoutQuantity": "Quantity",
    "testFlow.sampleBreakoutQuantityHelper":
      "Enter an integer greater than 0.",
    "testFlow.branchMatrixTitle": "Branch Matrix",
    "testFlow.branchMatrixSubtitle":
      "Drop requirements into branch row slots for this leg.",
    "testFlow.mainBranch": "Main branch",
    "testFlow.variantBranch": "Split branch",
    "testFlow.delete": "Delete",
    "testFlow.requirementsTitle": "Test Requirements",
    "testFlow.requirementsSubtitle":
      "Drag a requirement into a leg. The list mirrors the categorized requirements from the Test Requirements tab.",
    "testFlow.noRequirements": "No test requirements are available yet.",
    "testFlow.searchPlaceholder": "Search ...",
    "testFlow.clearSearch": "Clear search",
    "testFlow.noSearchResults": "No matching test requirements found.",
    "testFlow.unassigned": "Unassigned",
    "testFlow.expandCategory": "Expand category",
    "testFlow.collapseCategory": "Collapse category",
    "testFlow.save": "Save",
    "testFlow.dutLabel": "Nr. of DUTs",
    "testFlow.dutHelper": "Enter an integer greater than 0",
    "testFlow.assignedTitle": "{{legName}} Requirements",
    "testFlow.assignedSubtitle":
      "Drop requirements here to assign them to this leg. Drag within the list to reorder the execution sequence.",
    "testFlow.noAssigned":
      "No test requirements assigned to this leg yet.",
    "testFlow.invalidBranchDrop":
      "Split branches cannot use the first row.",
    "testFlow.blockedFirstSplitRow":
      "Blocked: split branches cannot use the first row.",
    "testFlow.blockedByMainBranch":
      "Blocked: {{branchName}} already has a test in this row.",
    "testFlow.blockedByVariantBranch":
      "Blocked: {{branchName}} uses this row.",
    "testFlow.blockedByOwnedRow":
      "Blocked: {{branchName}} already owns this row.",
    "testFlow.blockedByRightBranch":
      "Blocked: following rows cannot move left of {{branchName}}.",
    "testFlow.availableDropSlot": "Drop here",
    "testFlow.insertRowBelow": "Insert row below",
    "testFlow.removeEmptyRow": "Remove empty row",
    "testFlow.noLegs": "Add a leg to start building the test flow.",
    "testFlow.unnamedRequirement": "Unnamed requirement",
    "testFlow.untitledLeg": "Untitled Leg",
    "testFlow.deleteLastLegError": "At least one leg is required.",
    "testFlow.deleteLegSuccess": "{{legName}} deleted.",
    "testFlow.deleteLegLocalSuccess": "{{legName}} removed locally.",
    "testFlow.invalidDutError":
      "\"{{legName}}\" needs a DUT value greater than 0.",
    "testFlow.invalidSampleBreakoutError":
      "\"{{legName}}\" has invalid sample breakout values.",
    "testFlow.sampleBreakoutTotalError":
      "Sample breakout quantities for \"{{legName}}\" must equal the number of DUTs.",
    "testFlow.sampleBreakoutAllocationError":
      "Split branch quantities for \"{{legName}}\" cannot exceed the master branch quantity for a sample breakout.",
    "testFlow.sampleBreakoutRemainingError":
      "At least one sample breakout in \"{{legName}}\" must keep a quantity greater than 0 in the master branch.",
    "testFlow.saveSuccess": "Test flow configuration saved.",
    "testFlow.saveNoChanges": "No changes to save.",
    "testFlow.orderLabel": "#{{index}}",
  },
  de: {
    "testFlow.title": "Testablauf",
    "testFlow.subtitle":
      "Ordnen Sie Testanforderungen bearbeitbaren Legs zu, definieren Sie die DUT-Anzahl und ordnen Sie den Ablauf per Drag-and-drop.",
    "testFlow.addLeg": "Leg hinzufugen",
    "testFlow.addBranch": "Branch hinzufugen",
    "testFlow.deleteBranch": "Branch loschen",
    "testFlow.branchLabel": "Branch {{branchName}}",
    "testFlow.sampleBreakouts": "Sample Breakouts",
    "testFlow.sampleBreakoutsTitle": "Sample Breakouts",
    "testFlow.sampleBreakoutsSubtitle":
      "Erstellen Sie Sample Breakouts fur dieses Leg. Die Summe der Mengen muss der Anzahl der DUTs entsprechen.",
    "testFlow.addSampleBreakout": "Sample Breakout hinzufugen",
    "testFlow.noSampleBreakouts":
      "Fur dieses Leg wurden noch keine Sample Breakouts erstellt.",
    "testFlow.sampleBreakoutName": "Name",
    "testFlow.sampleBreakoutNameHelper":
      "Name ist erforderlich und darf maximal 255 Zeichen enthalten.",
    "testFlow.sampleBreakoutQuantity": "Menge",
    "testFlow.sampleBreakoutQuantityHelper":
      "Geben Sie eine ganze Zahl groser als 0 ein.",
    "testFlow.branchMatrixTitle": "Branch-Matrix",
    "testFlow.branchMatrixSubtitle":
      "Legen Sie Anforderungen in Branch-Zeilenslots fur dieses Leg ab.",
    "testFlow.mainBranch": "Haupt-Branch",
    "testFlow.variantBranch": "Split-Branch",
    "testFlow.delete": "Loschen",
    "testFlow.requirementsTitle": "Testanforderungen",
    "testFlow.requirementsSubtitle":
      "Ziehen Sie eine Anforderung in ein Leg. Die Liste entspricht den kategorisierten Anforderungen aus dem Tab Test Requirements.",
    "testFlow.noRequirements":
      "Derzeit sind keine Testanforderungen verfugbar.",
    "testFlow.searchPlaceholder": "Suchen ...",
    "testFlow.clearSearch": "Suche loschen",
    "testFlow.noSearchResults":
      "Keine passenden Testanforderungen gefunden.",
    "testFlow.unassigned": "Nicht zugewiesen",
    "testFlow.expandCategory": "Kategorie aufklappen",
    "testFlow.collapseCategory": "Kategorie zuklappen",
    "testFlow.save": "Speichern",
    "testFlow.dutLabel": "Anzahl DUTs",
    "testFlow.dutHelper": "Geben Sie eine ganze Zahl groser als 0 ein",
    "testFlow.assignedTitle": "{{legName}} Anforderungen",
    "testFlow.assignedSubtitle":
      "Legen Sie Anforderungen hier ab, um sie diesem Leg zuzuweisen. Ziehen Sie innerhalb der Liste, um die Reihenfolge anzupassen.",
    "testFlow.noAssigned":
      "Diesem Leg sind noch keine Testanforderungen zugewiesen.",
    "testFlow.invalidBranchDrop":
      "Split-Branches konnen die erste Zeile nicht verwenden.",
    "testFlow.blockedFirstSplitRow":
      "Blockiert: Split-Branches konnen die erste Zeile nicht verwenden.",
    "testFlow.blockedByMainBranch":
      "Blockiert: {{branchName}} hat bereits einen Test in dieser Zeile.",
    "testFlow.blockedByVariantBranch":
      "Blockiert: {{branchName}} verwendet diese Zeile.",
    "testFlow.blockedByOwnedRow":
      "Blockiert: {{branchName}} belegt diese Zeile bereits.",
    "testFlow.blockedByRightBranch":
      "Blockiert: Folgezeilen konnen nicht links von {{branchName}} liegen.",
    "testFlow.availableDropSlot": "Hier ablegen",
    "testFlow.insertRowBelow": "Zeile darunter einfugen",
    "testFlow.removeEmptyRow": "Leere Zeile entfernen",
    "testFlow.noLegs":
      "Fugen Sie ein Leg hinzu, um den Testablauf zu erstellen.",
    "testFlow.unnamedRequirement": "Unbenannte Anforderung",
    "testFlow.untitledLeg": "Unbenanntes Leg",
    "testFlow.deleteLastLegError":
      "Mindestens ein Leg muss vorhanden sein.",
    "testFlow.deleteLegSuccess": "{{legName}} wurde geloscht.",
    "testFlow.deleteLegLocalSuccess": "{{legName}} wurde lokal entfernt.",
    "testFlow.invalidDutError":
      "\"{{legName}}\" benotigt einen DUT-Wert groser als 0.",
    "testFlow.invalidSampleBreakoutError":
      "\"{{legName}}\" enthalt ungultige Sample-Breakout-Werte.",
    "testFlow.sampleBreakoutTotalError":
      "Die Sample-Breakout-Mengen fur \"{{legName}}\" mussen der Anzahl der DUTs entsprechen.",
    "testFlow.sampleBreakoutAllocationError":
      "Die Mengen der Split-Branches fur \"{{legName}}\" durfen die Menge des Haupt-Branches pro Sample Breakout nicht uberschreiten.",
    "testFlow.sampleBreakoutRemainingError":
      "Mindestens ein Sample Breakout in \"{{legName}}\" muss im Haupt-Branch eine Menge groser als 0 behalten.",
    "testFlow.saveSuccess":
      "Die Testablauf-Konfiguration wurde gespeichert.",
    "testFlow.saveNoChanges": "Keine Anderungen zum Speichern.",
    "testFlow.orderLabel": "#{{index}}",
  },
};

type I18nContextValue = {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  t: (key: string, variables?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const STORAGE_KEY = "app_language";

const getInitialLanguage = (): SupportedLanguage => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "de") return stored;

  const browserLanguage = navigator.language.toLowerCase();
  if (browserLanguage.startsWith("de")) return "de";
  return "en";
};

const interpolate = (
  template: string,
  variables?: Record<string, string | number>
) => {
  if (!variables) return template;

  return Object.entries(variables).reduce((acc, [key, value]) => {
    return acc.replaceAll(`{{${key}}}`, String(value));
  }, template);
};

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(
    getInitialLanguage
  );

  const setLanguage = (nextLanguage: SupportedLanguage) => {
    setLanguageState(nextLanguage);
    localStorage.setItem(STORAGE_KEY, nextLanguage);
  };

  const value = useMemo<I18nContextValue>(() => {
    return {
      language,
      setLanguage,
      t: (key, variables) => {
        const template =
          dictionaries[language][key] ?? dictionaries.en[key] ?? key;
        return interpolate(template, variables);
      },
    };
  }, [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return context;
}
